/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { stkAPI, locationsAPI, filesAPI } from '../lib/api';
import DOMPurify from 'dompurify';
import STKPendingDocRequests from '../components/STKPendingDocRequests';
import { formatDateTime, STK_STATUS, formatApiErrorDetail, STK_KURUM_TURLERI, STK_KURUM_TURU_LABELS } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Eye,
  ChevronLeft,
  ChevronRight,
  Check,
  Upload,
  FileText,
  Loader2,
  X,
  AlertCircle,
  CheckCircle,
  Clock,
  Trash2,
  Send,
  Paperclip,
  ShieldCheck,
  Search,
  TrendingUp,
  Download,
  Mail,
} from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';

const STKListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageTitle('STK Basvurulari');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  // Gelismis filtreler
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [kurumTuruFilter, setKurumTuruFilter] = useState('');
  const [faaliyetAlaniFilter, setFaaliyetAlaniFilter] = useState('');
  const [ilFilter, setIlFilter] = useState('');
  const [ilceFilter, setIlceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  // Toplu e-posta
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkBody, setBulkBody] = useState('<p>Sayın {{kurum_adi}},</p>\n<p>Merhaba,</p>\n<p>... toplantı içeriği ...</p>\n<p>Saygılarımızla,<br/>Kent Konseyi</p>');
  const [bulkSending, setBulkSending] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyDetail, setHistoryDetail] = useState(null);
  const limit = 10;

  const isAdmin = ['admin', 'genel_sekreter'].includes(user?.role);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchText), 350);
    return () => clearTimeout(t);
  }, [searchText]);

  useEffect(() => {
    loadApplications();
  }, [page, statusFilter, searchDebounced, kurumTuruFilter, faaliyetAlaniFilter, ilFilter, ilceFilter, dateFrom, dateTo]);

  useEffect(() => {
    locationsAPI.getIller().then(r => setIller(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (ilFilter) {
      locationsAPI.getIlceler(ilFilter).then(r => setIlceler(r.data || [])).catch(() => {});
    } else {
      setIlceler([]); setIlceFilter('');
    }
  }, [ilFilter]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * limit,
        limit,
        status: statusFilter || undefined,
        search: searchDebounced || undefined,
        kurum_turu: kurumTuruFilter || undefined,
        faaliyet_alani: faaliyetAlaniFilter || undefined,
        il_id: ilFilter || undefined,
        ilce_id: ilceFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      };
      const response = await stkAPI.getApplications(params);
      setApplications(response.data.applications);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Başvurular yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = STK_STATUS[status] || { label: status, color: 'default' };
    const colorClasses = {
      success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      warning: 'bg-amber-50 text-amber-700 border-amber-200',
      error: 'bg-red-50 text-red-700 border-red-200',
      info: 'bg-blue-50 text-blue-700 border-blue-200',
      default: 'bg-slate-50 text-slate-700 border-slate-200',
    };
    return (
      <Badge className={`${colorClasses[statusInfo.color]} border`}>
        {statusInfo.label}
      </Badge>
    );
  };

  const totalPages = Math.ceil(total / limit);

  // Stats from loaded data
  const stats = useMemo(() => ({
    total: total,
    beklemede: applications.filter(a => a.status === 'inceleniyor' || a.status === 'taslak').length,
    onaylandi: applications.filter(a => a.status === 'onaylandi').length,
    reddedildi: applications.filter(a => a.status === 'reddedildi').length,
  }), [applications, total]);

  const statusTabs = [
    { value: '', label: 'Tumu' },
    { value: 'inceleniyor', label: 'Inceleniyor' },
    { value: 'taslak', label: 'Taslak' },
    { value: 'onaylandi', label: 'Onaylandi' },
    { value: 'reddedildi', label: 'Reddedildi' },
    { value: 'eksik_belge', label: 'Eksik Belge' },
  ];

  const currentFilters = () => ({
    status: statusFilter || undefined,
    search: searchDebounced || undefined,
    kurum_turu: kurumTuruFilter || undefined,
    faaliyet_alani: faaliyetAlaniFilter || undefined,
    il_id: ilFilter || undefined,
    ilce_id: ilceFilter || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const handleSendBulkEmail = async () => {
    if (!bulkSubject.trim()) { toast.error('Konu zorunlu'); return; }
    if (!bulkBody.trim()) { toast.error('Icerik zorunlu'); return; }
    setBulkSending(true);
    try {
      const r = await stkAPI.sendBulkEmail({
        subject: bulkSubject, body_html: bulkBody, ...currentFilters(),
      });
      toast.success(r.data.message);
      setBulkEmailOpen(false);
      setBulkSubject('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Toplu gonderim basarisiz');
    } finally {
      setBulkSending(false);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await stkAPI.getBulkEmailHistory({ limit: 50 });
      setHistory(r.data.items || []);
    } catch { toast.error('Gecmis yuklenemedi'); }
  };

  const openHistory = () => {
    setHistoryOpen(true);
    loadHistory();
  };

  return (
    <div className="space-y-6" data-testid="stk-list-page">
      {/* STK kullanicisinin bekleyen belge talepleri (admin gormez) */}
      {user?.role === 'stk' && <STKPendingDocRequests />}
      {/* Page Header */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-teal-800 via-cyan-900 to-teal-800 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">STK Basvurulari</h1>
                <p className="text-sm text-white/50">Sivil toplum kurulusu basvurularini yonetin</p>
              </div>
            </div>
            {!isAdmin && (
              <Button onClick={() => navigate('/panel/stk/basvuru')} className="bg-white/10 hover:bg-white/20 text-white border border-white/20" data-testid="new-stk-application-btn">
                <Plus className="w-4 h-4 mr-2" /> Yeni Basvuru
              </Button>
            )}
            {isAdmin && (
              <div className="flex gap-2">
                <Button onClick={() => setBulkEmailOpen(true)} className="bg-white/10 hover:bg-white/20 text-white border border-white/20" data-testid="bulk-email-btn">
                  <Mail className="w-4 h-4 mr-2" /> Toplu E-posta
                </Button>
                <Button onClick={openHistory} variant="ghost" className="bg-white/5 hover:bg-white/15 text-white border border-white/15" data-testid="email-history-btn">
                  <Clock className="w-4 h-4 mr-2" /> Gecmis
                </Button>
                <a href={stkAPI.exportUrl(statusFilter || '')} className="inline-flex items-center h-9 px-4 text-xs font-medium rounded-md bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors gap-1.5" data-testid="export-stk-btn">
                  <Download className="w-3.5 h-3.5" /> Excel'e Aktar
                </a>
                <a href={stkAPI.bulkDownloadUrl(statusFilter || '')} className="inline-flex items-center h-9 px-4 text-xs font-medium rounded-md bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-400/40 transition-colors gap-1.5" data-testid="bulk-download-stk-btn" title="Tum belgeleri ZIP olarak indir">
                  <Download className="w-3.5 h-3.5" /> Belgeleri ZIP İndir
                </a>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-teal-50"><Building2 className="w-5 h-5 text-teal-600" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-xs text-slate-500">Toplam Basvuru</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-blue-50"><Clock className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.beklemede}</p>
              <p className="text-xs text-slate-500">Bekleyen</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-50"><CheckCircle className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.onaylandi}</p>
              <p className="text-xs text-slate-500">Onaylanan</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-red-50"><X className="w-5 h-5 text-red-600" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.reddedildi}</p>
              <p className="text-xs text-slate-500">Reddedilen</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pill Tabs */}
      <div className="flex gap-1.5 flex-wrap items-center" data-testid="stk-status-tabs">
        {statusTabs.map(t => (
          <button
            key={t.value}
            onClick={() => { setStatusFilter(t.value); setPage(1); }}
            data-testid={`stk-tab-${t.value || 'all'}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              statusFilter === t.value
                ? 'bg-teal-600 text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
              placeholder="Kurum adi, sicil, vergi no, temsilci..."
              className="pl-9 w-72 h-9"
              data-testid="stk-search-input"
            />
          </div>
          <Button
            variant={showAdvanced ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowAdvanced(v => !v)}
            data-testid="toggle-advanced-filter-btn"
            className={showAdvanced ? 'bg-teal-600 hover:bg-teal-700' : ''}
          >
            Gelismis Filtre
          </Button>
          {(searchText || kurumTuruFilter || faaliyetAlaniFilter || ilFilter || ilceFilter || dateFrom || dateTo) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchText(''); setKurumTuruFilter(''); setFaaliyetAlaniFilter('');
                setIlFilter(''); setIlceFilter(''); setDateFrom(''); setDateTo('');
                setPage(1);
              }}
              data-testid="clear-filters-btn"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Temizle
            </Button>
          )}
        </div>
      </div>

      {/* Advanced Filter Panel */}
      {showAdvanced && (
        <Card className="shadow-sm border-teal-100 bg-teal-50/30" data-testid="advanced-filter-panel">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Kurum Turu</Label>
                <Select value={kurumTuruFilter || 'all'} onValueChange={v => { setKurumTuruFilter(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger data-testid="filter-kurum-turu"><SelectValue placeholder="Tumu" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tumu</SelectItem>
                    {STK_KURUM_TURLERI.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Faaliyet Alani</Label>
                <Input
                  value={faaliyetAlaniFilter}
                  onChange={e => { setFaaliyetAlaniFilter(e.target.value); setPage(1); }}
                  placeholder="Egitim, Cevre, Saglik..."
                  data-testid="filter-faaliyet-alani"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Il</Label>
                <Select value={ilFilter || 'all'} onValueChange={v => { setIlFilter(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger data-testid="filter-il"><SelectValue placeholder="Tumu" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tumu</SelectItem>
                    {iller.map(il => <SelectItem key={il.id} value={il.id}>{il.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Ilce</Label>
                <Select value={ilceFilter || 'all'} onValueChange={v => { setIlceFilter(v === 'all' ? '' : v); setPage(1); }} disabled={!ilFilter}>
                  <SelectTrigger data-testid="filter-ilce"><SelectValue placeholder={ilFilter ? 'Tumu' : 'Once il secin'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tumu</SelectItem>
                    {ilceler.map(ilce => <SelectItem key={ilce.id} value={ilce.id}>{ilce.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Baslangic Tarihi</Label>
                <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} data-testid="filter-date-from" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Bitis Tarihi</Label>
                <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} data-testid="filter-date-to" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Applications Table */}
      <Card className="shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : applications.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="font-semibold">Kurum Adı</TableHead>
                    <TableHead className="font-semibold">Tür</TableHead>
                    <TableHead className="font-semibold">Durum</TableHead>
                    <TableHead className="font-semibold">Tarih</TableHead>
                    <TableHead className="font-semibold text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map((app) => (
                    <TableRow key={app.id} className="hover:bg-slate-50/50">
                      <TableCell className="font-medium">{app.kurum_adi}</TableCell>
                      <TableCell className="capitalize">{app.kurum_turu}</TableCell>
                      <TableCell>{getStatusBadge(app.status)}</TableCell>
                      <TableCell className="text-slate-500">
                        {formatDateTime(app.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/panel/stk/${app.id}`)}
                          data-testid={`view-stk-${app.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Görüntüle
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-slate-500">
                    Toplam {total} kayıt, Sayfa {page}/{totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <Building2 className="w-8 h-8 text-slate-300" />
              </div>
              <h3 className="text-lg font-medium text-slate-900">Henuz basvuru yok</h3>
              <p className="text-slate-500 mt-1">
                {isAdmin ? 'Henuz STK basvurusu bulunmuyor' : 'Ilk basvurunuzu olusturmak icin butona tiklayin'}
              </p>
              {!isAdmin && (
                <Button onClick={() => navigate('/panel/stk/basvuru')} className="mt-4 bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Yeni Basvuru
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Toplu E-posta Dialog */}
      <Dialog open={bulkEmailOpen} onOpenChange={setBulkEmailOpen}>
        <DialogContent className="max-w-2xl" data-testid="bulk-email-dialog">
          <DialogHeader>
            <DialogTitle>Toplu E-posta Gonder</DialogTitle>
            <DialogDescription>
              Aktif filtrelere uyan {total} STK'ya e-posta gonderilecek. Icerikte <code>{'{{kurum_adi}}'}</code> degiskenini kullanabilirsiniz.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Konu *</Label>
              <Input value={bulkSubject} onChange={(e) => setBulkSubject(e.target.value)} placeholder="E-posta konusu" data-testid="bulk-email-subject" />
            </div>
            <div>
              <Label>Icerik (HTML) *</Label>
              <Textarea value={bulkBody} onChange={(e) => setBulkBody(e.target.value)} rows={10} className="font-mono text-xs" data-testid="bulk-email-body" />
              <p className="text-[11px] text-slate-500 mt-1">Ipucu: <code>{'{{kurum_adi}}'}</code> kurum adiyla yer degistirilir.</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
              <div className="font-medium mb-1">Kullanilacak filtreler:</div>
              <ul className="list-disc list-inside space-y-0.5">
                {statusFilter && <li>Durum: <b>{statusFilter}</b></li>}
                {searchDebounced && <li>Arama: <b>{searchDebounced}</b></li>}
                {kurumTuruFilter && <li>Kurum Turu: <b>{STK_KURUM_TURU_LABELS[kurumTuruFilter] || kurumTuruFilter}</b></li>}
                {faaliyetAlaniFilter && <li>Faaliyet: <b>{faaliyetAlaniFilter}</b></li>}
                {ilFilter && <li>Il ID: <b>{ilFilter}</b></li>}
                {ilceFilter && <li>Ilce ID: <b>{ilceFilter}</b></li>}
                {dateFrom && <li>Baslangic: <b>{dateFrom}</b></li>}
                {dateTo && <li>Bitis: <b>{dateTo}</b></li>}
                {!statusFilter && !searchDebounced && !kurumTuruFilter && !faaliyetAlaniFilter && !ilFilter && !ilceFilter && !dateFrom && !dateTo && <li>Filtre yok - <b>tum STK'lar</b> (taslak haric)</li>}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEmailOpen(false)} disabled={bulkSending} data-testid="bulk-email-cancel">
              Iptal
            </Button>
            <Button onClick={handleSendBulkEmail} disabled={bulkSending} className="bg-teal-600 hover:bg-teal-700" data-testid="bulk-email-send">
              {bulkSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gonderiliyor...</> : <><Send className="w-4 h-4 mr-2" /> Gonder</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gecmis Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="email-history-dialog">
          <DialogHeader>
            <DialogTitle>Toplu E-posta Gecmisi</DialogTitle>
            <DialogDescription>Son 50 gonderim</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {history.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">Henuz gonderim yok</p>}
            {history.map((h) => (
              <Card key={h.id} className="border border-slate-200">
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{h.subject}</div>
                    <div className="text-xs text-slate-500 flex gap-3 flex-wrap mt-1">
                      <span>Alici: <b>{h.recipient_count}</b></span>
                      <span className="text-green-600">Basarili: {h.sent_count}</span>
                      {h.failed_count > 0 && <span className="text-red-600">Basarisiz: {h.failed_count}</span>}
                      <span>{formatDateTime(h.sent_at)}</span>
                      {h.sent_by_name && <span>Gonderen: {h.sent_by_name}</span>}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { setHistoryOpen(false); setHistoryDetail(h); }} data-testid={`history-detail-${h.id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Detay
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Gecmis Detay Dialog */}
      <Dialog open={!!historyDetail} onOpenChange={(o) => { if (!o) { setHistoryDetail(null); setHistoryOpen(true); } }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="email-history-detail-dialog">
          <DialogHeader>
            <DialogTitle>{historyDetail?.subject}</DialogTitle>
            <DialogDescription>{historyDetail && formatDateTime(historyDetail.sent_at)} - {historyDetail?.sent_by_name}</DialogDescription>
          </DialogHeader>
          {historyDetail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-slate-50 p-2 rounded"><b>Alici:</b> {historyDetail.recipient_count}</div>
                <div className="bg-green-50 p-2 rounded"><b>Basarili:</b> {historyDetail.sent_count}</div>
                <div className="bg-red-50 p-2 rounded"><b>Basarisiz:</b> {historyDetail.failed_count}</div>
              </div>
              <div>
                <Label className="text-xs">Icerik</Label>
                <div className="border rounded p-3 bg-white max-h-60 overflow-auto text-xs" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(historyDetail.body_html || '') }} />
              </div>
              <div>
                <Label className="text-xs">Alici E-postalari ({historyDetail.recipient_emails?.length || 0})</Label>
                <div className="border rounded p-2 bg-slate-50 max-h-40 overflow-auto text-xs font-mono">
                  {(historyDetail.recipient_emails || []).join(', ')}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const STKApplicationForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageTitle('STK Basvuru Formu');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [applicationId, setApplicationId] = useState(null);
  
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);

  const emptyForm = {
    kurum_adi: '',
    kurum_turu: '',
    kurum_kisa_adi: '',
    kurulus_tarihi: '',
    sicil_no: '',
    vergi_no: '',
    faaliyet_alani: '',
    adres: '',
    il_id: '',
    ilce_id: '',
    telefon: '',
    email: '',
    web_sitesi: '',
    temsilci_adi: '',
    temsilci_soyadi: '',
    temsilci_tc: '',
    temsilci_gorevi: '',
    temsilci_telefon: '',
    temsilci_email: '',
    yedek_temsilci_adi: '',
    yedek_temsilci_soyadi: '',
    yedek_temsilci_tc: '',
    yedek_temsilci_gorevi: '',
    yedek_temsilci_telefon: '',
    yedek_temsilci_email: '',
    yonetim_kurulu: [],
    uye_sayisi: '',
    aktif_proje_sayisi: '',
    faaliyet_ozeti: '',
    katilim_amaci: '',
    katki_alanlari: [],
    belgeler: [],
    current_step: 1,
  };
  
  const [formData, setFormData] = useState(emptyForm);

  const steps = [
    { number: 1, title: 'Temel Bilgiler' },
    { number: 2, title: 'Kurumsal' },
    { number: 3, title: 'İletişim' },
    { number: 4, title: 'Temsilci' },
    { number: 5, title: 'Yönetim' },
    { number: 6, title: 'Faaliyet' },
    { number: 7, title: 'Katılım' },
    { number: 8, title: 'Belgeler' },
    { number: 9, title: 'Önizleme' },
  ];

  const kurumTurleri = STK_KURUM_TURLERI;

  const katkiAlanlari = [
    'Çevre', 'Eğitim', 'Sağlık', 'Kültür-Sanat', 'Spor', 'Sosyal Hizmetler',
    'Kadın Hakları', 'Çocuk Hakları', 'Engelli Hakları', 'Gençlik', 'Yaşlı Bakımı',
  ];

  useEffect(() => {
    loadIller();
    loadExistingDraft();
  }, []);

  useEffect(() => {
    if (formData.il_id) {
      loadIlceler(formData.il_id);
    }
  }, [formData.il_id]);

  const loadExistingDraft = async () => {
    setInitialLoading(true);
    try {
      const response = await stkAPI.getMyDraft();
      const draft = response.data;
      if (draft && draft.id) {
        setApplicationId(draft.id);
        setCurrentStep(draft.current_step || 1);
        setFormData({
          kurum_adi: draft.kurum_adi || '',
          kurum_turu: draft.kurum_turu || '',
          kurum_kisa_adi: draft.kurum_kisa_adi || '',
          kurulus_tarihi: draft.kurulus_tarihi || '',
          sicil_no: draft.sicil_no || '',
          vergi_no: draft.vergi_no || '',
          faaliyet_alani: draft.faaliyet_alani || '',
          adres: draft.adres || '',
          il_id: draft.il_id || '',
          ilce_id: draft.ilce_id || '',
          telefon: draft.telefon || '',
          email: draft.email || '',
          web_sitesi: draft.web_sitesi || '',
          temsilci_adi: draft.temsilci_adi || '',
          temsilci_soyadi: draft.temsilci_soyadi || '',
          temsilci_tc: draft.temsilci_tc || '',
          temsilci_gorevi: draft.temsilci_gorevi || '',
          temsilci_telefon: draft.temsilci_telefon || '',
          temsilci_email: draft.temsilci_email || '',
          yedek_temsilci_adi: draft.yedek_temsilci_adi || '',
          yedek_temsilci_soyadi: draft.yedek_temsilci_soyadi || '',
          yedek_temsilci_tc: draft.yedek_temsilci_tc || '',
          yedek_temsilci_gorevi: draft.yedek_temsilci_gorevi || '',
          yedek_temsilci_telefon: draft.yedek_temsilci_telefon || '',
          yedek_temsilci_email: draft.yedek_temsilci_email || '',
          yonetim_kurulu: draft.yonetim_kurulu || [],
          uye_sayisi: draft.uye_sayisi || '',
          aktif_proje_sayisi: draft.aktif_proje_sayisi || '',
          faaliyet_ozeti: draft.faaliyet_ozeti || '',
          katilim_amaci: draft.katilim_amaci || '',
          katki_alanlari: draft.katki_alanlari || [],
          belgeler: draft.belgeler || [],
          current_step: draft.current_step || 1,
        });
        toast.info('Mevcut taslak başvurunuz yüklendi');
      }
    } catch (error) {
      // No existing draft - that's fine, start fresh
    } finally {
      setInitialLoading(false);
    }
  };

  const loadIller = async () => {
    try {
      const response = await locationsAPI.getIller();
      setIller(response.data);
    } catch (error) {
      console.error('Failed to load iller:', error);
    }
  };

  const loadIlceler = async (ilId) => {
    try {
      const response = await locationsAPI.getIlceler(ilId);
      setIlceler(response.data);
    } catch (error) {
      console.error('Failed to load ilceler:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = { ...formData, current_step: currentStep };
      
      if (applicationId) {
        await stkAPI.updateApplication(applicationId, dataToSave);
        toast.success('Başvuru kaydedildi');
      } else {
        const response = await stkAPI.createApplication(dataToSave);
        const newId = response.data.id;
        setApplicationId(newId);
        toast.success('Başvuru oluşturuldu');
      }
      return true;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Kaydetme başarısız');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Adim bazli zorunlu alan dogrulamasi
  const validateStep = (step) => {
    const errors = [];
    if (step === 1) {
      if (!formData.kurum_adi?.trim()) errors.push('Kurum Adı');
      if (!formData.kurum_turu) errors.push('Kurum Türü');
    }
    if (step === 3) {
      // Email formati kontrolu (girilmisse)
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.push('E-posta formatı (iletişim e-postası geçersiz)');
      }
    }
    if (step === 4) {
      if (!formData.temsilci_adi?.trim()) errors.push('Temsilci Adı');
      if (!formData.temsilci_soyadi?.trim()) errors.push('Temsilci Soyadı');
      if (!formData.temsilci_tc?.trim()) errors.push('Temsilci TC Kimlik');
      else if (!/^\d{11}$/.test(formData.temsilci_tc)) {
        errors.push('Temsilci TC Kimlik 11 haneli rakam olmalı');
      }
      if (!formData.temsilci_email?.trim()) errors.push('Temsilci E-postası');
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.temsilci_email)) {
        errors.push('Temsilci e-posta formatı geçersiz');
      }
    }
    if (step === 8) {
      const derbis = formData.belgeler?.find(b => b.belge_turu === 'derbis');
      const yk = formData.belgeler?.find(b => b.belge_turu === 'yonetim_kurulu_karari');
      const yetkili = formData.belgeler?.find(b => b.belge_turu === 'yetkili_temsilci');
      if (!derbis) errors.push('Faaliyet Belgesi (DERBİS)');
      if (!yk) errors.push('Yönetim Kurulu Kararı');
      if (!yetkili) errors.push('Yetkili Temsilci Belgesi');
    }
    return errors;
  };

  const handleNext = async () => {
    const missing = validateStep(currentStep);
    if (missing.length > 0) {
      toast.error(`Lütfen zorunlu alanları doldurun: ${missing.join(', ')}`);
      return;
    }
    const success = await handleSave();
    if (success && currentStep < 9) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!applicationId) {
      toast.error('Önce başvuruyu kaydedin');
      return;
    }
    // Tum adimlardaki zorunlu alanlari topla
    const allMissing = [];
    for (let s = 1; s <= 8; s++) {
      const m = validateStep(s);
      if (m.length) allMissing.push(`Adım ${s}: ${m.join(', ')}`);
    }
    if (allMissing.length > 0) {
      toast.error(
        <div>
          <div className="font-semibold mb-1">Eksik zorunlu alanlar:</div>
          {allMissing.map((l, i) => (<div key={i} className="text-xs">• {l}</div>))}
        </div>,
        { duration: 6000 }
      );
      return;
    }
    
    setLoading(true);
    try {
      await stkAPI.submitApplication(applicationId);
      toast.success('Başvurunuz gönderildi');
      navigate('/panel/stk');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Başvuru gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const response = await filesAPI.upload(file);
      setFormData({
        ...formData,
        belgeler: [...formData.belgeler, { id: response.data.file_id, name: file.name }],
      });
      toast.success('Dosya yüklendi');
    } catch (error) {
      toast.error('Dosya yüklenemedi');
    }
  };

  const removeFile = (fileId) => {
    setFormData({
      ...formData,
      belgeler: formData.belgeler.filter(b => b.id !== fileId),
    });
  };

  const addYonetimKuruluMember = () => {
    setFormData({
      ...formData,
      yonetim_kurulu: [...formData.yonetim_kurulu, { ad: '', soyad: '', gorev: '', telefon: '' }],
    });
  };

  const updateYonetimKuruluMember = (index, field, value) => {
    const updated = [...formData.yonetim_kurulu];
    updated[index][field] = value;
    setFormData({ ...formData, yonetim_kurulu: updated });
  };

  const removeYonetimKuruluMember = (index) => {
    setFormData({
      ...formData,
      yonetim_kurulu: formData.yonetim_kurulu.filter((_, i) => i !== index),
    });
  };

  const toggleKatkiAlani = (alan) => {
    const current = formData.katki_alanlari || [];
    if (current.includes(alan)) {
      setFormData({ ...formData, katki_alanlari: current.filter(a => a !== alan) });
    } else {
      setFormData({ ...formData, katki_alanlari: [...current, alan] });
    }
  };

  const progress = (currentStep / 9) * 100;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="stk-application-form">
      {/* Back */}
      <div className="flex items-center">
        <Button variant="ghost" size="sm" onClick={() => navigate('/panel/stk')} className="text-slate-600 hover:text-slate-900">
          <ChevronLeft className="w-4 h-4 mr-1" /> STK Basvurulari
        </Button>
      </div>

      {/* Page Header */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-teal-800 via-cyan-900 to-teal-800 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">STK Basvurusu</h1>
              <p className="text-sm text-white/50">Sivil toplum kurulusunuzu kaydedin</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Progress Stepper */}
      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-700">
              Adim {currentStep} / 9: {steps[currentStep - 1].title}
            </span>
            <span className="text-sm font-semibold text-teal-600">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          
          {/* Step indicators */}
          <div className="hidden sm:flex justify-between mt-4">
            {steps.map((step) => (
              <button
                key={step.number}
                onClick={() => setCurrentStep(step.number)}
                className={`flex flex-col items-center gap-1.5 ${
                  step.number === currentStep
                    ? 'text-teal-600'
                    : step.number < currentStep
                    ? 'text-emerald-600'
                    : 'text-slate-400'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    step.number === currentStep
                      ? 'bg-teal-600 text-white shadow-sm ring-2 ring-teal-200'
                      : step.number < currentStep
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {step.number < currentStep ? <Check className="w-4 h-4" /> : step.number}
                </div>
                <span className="text-xs hidden md:block">{step.title}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Form Content */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          {initialLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <p className="text-slate-500 mt-3">Mevcut başvuru kontrol ediliyor...</p>
            </div>
          ) : (
          <>
          {/* Step 1: Temel Bilgiler */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-900">Temel Bilgiler</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="kurum_adi">Kurum Adı *</Label>
                  <Input
                    id="kurum_adi"
                    value={formData.kurum_adi}
                    onChange={(e) => setFormData({ ...formData, kurum_adi: e.target.value })}
                    placeholder="Kurum tam adı"
                    data-testid="kurum-adi-input"
                  />
                </div>
                <div>
                  <Label htmlFor="kurum_turu">Kurum Türü <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.kurum_turu}
                    onValueChange={(v) => setFormData({ ...formData, kurum_turu: v })}
                  >
                    <SelectTrigger data-testid="kurum-turu-select">
                      <SelectValue placeholder="Kurum türü seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {kurumTurleri.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="kurum_kisa_adi">Kısa Adı</Label>
                  <Input
                    id="kurum_kisa_adi"
                    value={formData.kurum_kisa_adi}
                    onChange={(e) => setFormData({ ...formData, kurum_kisa_adi: e.target.value })}
                    placeholder="Varsa kısaltma"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Kurumsal Bilgiler */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-900">Kurumsal Bilgiler</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="kurulus_tarihi">Kuruluş Tarihi</Label>
                  <Input
                    id="kurulus_tarihi"
                    type="date"
                    value={formData.kurulus_tarihi}
                    onChange={(e) => setFormData({ ...formData, kurulus_tarihi: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="sicil_no">Sicil No</Label>
                  <Input
                    id="sicil_no"
                    value={formData.sicil_no}
                    onChange={(e) => setFormData({ ...formData, sicil_no: e.target.value })}
                    placeholder="Dernek/Vakıf sicil numarası"
                  />
                </div>
                <div>
                  <Label htmlFor="vergi_no">Vergi No</Label>
                  <Input
                    id="vergi_no"
                    value={formData.vergi_no}
                    onChange={(e) => setFormData({ ...formData, vergi_no: e.target.value })}
                    placeholder="Vergi numarası"
                  />
                </div>
                <div>
                  <Label htmlFor="faaliyet_alani">Faaliyet Alanı</Label>
                  <Input
                    id="faaliyet_alani"
                    value={formData.faaliyet_alani}
                    onChange={(e) => setFormData({ ...formData, faaliyet_alani: e.target.value })}
                    placeholder="Ana faaliyet alanı"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: İletişim Bilgileri */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-900">İletişim Bilgileri</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="adres">Adres</Label>
                  <Textarea
                    id="adres"
                    value={formData.adres}
                    onChange={(e) => setFormData({ ...formData, adres: e.target.value })}
                    placeholder="Açık adres"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="il">İl</Label>
                    <Select
                      value={formData.il_id}
                      onValueChange={(v) => setFormData({ ...formData, il_id: v, ilce_id: '' })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="İl seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {iller.map((il) => (
                          <SelectItem key={il.id} value={il.id}>{il.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="ilce">İlçe</Label>
                    <Select
                      value={formData.ilce_id}
                      onValueChange={(v) => setFormData({ ...formData, ilce_id: v })}
                      disabled={!formData.il_id}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="İlçe seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {ilceler.map((ilce) => (
                          <SelectItem key={ilce.id} value={ilce.id}>{ilce.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="telefon">Telefon</Label>
                    <Input
                      id="telefon"
                      value={formData.telefon}
                      onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
                      placeholder="0XXX XXX XX XX"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">E-posta</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="iletisim@kurum.org"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="web_sitesi">Web Sitesi</Label>
                  <Input
                    id="web_sitesi"
                    value={formData.web_sitesi}
                    onChange={(e) => setFormData({ ...formData, web_sitesi: e.target.value })}
                    placeholder="https://www.kurum.org"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Temsilci Bilgileri */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-900">Asıl Temsilci Bilgileri</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="temsilci_adi">Ad <span className="text-red-500">*</span></Label>
                    <Input
                      id="temsilci_adi"
                      value={formData.temsilci_adi}
                      onChange={(e) => setFormData({ ...formData, temsilci_adi: e.target.value })}
                      placeholder="Temsilci adı"
                      data-testid="temsilci-adi-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="temsilci_soyadi">Soyad <span className="text-red-500">*</span></Label>
                    <Input
                      id="temsilci_soyadi"
                      value={formData.temsilci_soyadi}
                      onChange={(e) => setFormData({ ...formData, temsilci_soyadi: e.target.value })}
                      placeholder="Temsilci soyadı"
                    />
                  </div>
                  <div>
                    <Label htmlFor="temsilci_tc">TC Kimlik <span className="text-red-500">*</span></Label>
                    <Input
                      id="temsilci_tc"
                      value={formData.temsilci_tc || ''}
                      onChange={(e) => setFormData({ ...formData, temsilci_tc: e.target.value.replace(/[^0-9]/g, '') })}
                      placeholder="11 haneli TC No"
                      maxLength={11}
                      data-testid="temsilci-tc-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="temsilci_gorevi">Görevi</Label>
                    <Input
                      id="temsilci_gorevi"
                      value={formData.temsilci_gorevi}
                      onChange={(e) => setFormData({ ...formData, temsilci_gorevi: e.target.value })}
                      placeholder="Kurumdaki görevi"
                    />
                  </div>
                  <div>
                    <Label htmlFor="temsilci_telefon">Telefon</Label>
                    <Input
                      id="temsilci_telefon"
                      value={formData.temsilci_telefon}
                      onChange={(e) => setFormData({ ...formData, temsilci_telefon: e.target.value })}
                      placeholder="05XX XXX XX XX"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="temsilci_email">E-posta <span className="text-red-500">*</span></Label>
                    <Input
                      id="temsilci_email"
                      type="email"
                      value={formData.temsilci_email}
                      onChange={(e) => setFormData({ ...formData, temsilci_email: e.target.value })}
                      placeholder="temsilci@email.com"
                      data-testid="temsilci-email-input"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">
                  Yedek Temsilci Bilgileri <span className="text-sm font-normal text-slate-500">(opsiyonel)</span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="yedek_temsilci_adi">Ad</Label>
                    <Input
                      id="yedek_temsilci_adi"
                      value={formData.yedek_temsilci_adi || ''}
                      onChange={(e) => setFormData({ ...formData, yedek_temsilci_adi: e.target.value })}
                      placeholder="Yedek temsilci adı"
                      data-testid="yedek-temsilci-adi-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="yedek_temsilci_soyadi">Soyad</Label>
                    <Input
                      id="yedek_temsilci_soyadi"
                      value={formData.yedek_temsilci_soyadi || ''}
                      onChange={(e) => setFormData({ ...formData, yedek_temsilci_soyadi: e.target.value })}
                      placeholder="Yedek temsilci soyadı"
                    />
                  </div>
                  <div>
                    <Label htmlFor="yedek_temsilci_tc">TC Kimlik</Label>
                    <Input
                      id="yedek_temsilci_tc"
                      value={formData.yedek_temsilci_tc || ''}
                      onChange={(e) => setFormData({ ...formData, yedek_temsilci_tc: e.target.value })}
                      placeholder="11 haneli TC No"
                      maxLength={11}
                    />
                  </div>
                  <div>
                    <Label htmlFor="yedek_temsilci_gorevi">Görevi</Label>
                    <Input
                      id="yedek_temsilci_gorevi"
                      value={formData.yedek_temsilci_gorevi || ''}
                      onChange={(e) => setFormData({ ...formData, yedek_temsilci_gorevi: e.target.value })}
                      placeholder="Kurumdaki görevi"
                    />
                  </div>
                  <div>
                    <Label htmlFor="yedek_temsilci_telefon">Telefon</Label>
                    <Input
                      id="yedek_temsilci_telefon"
                      value={formData.yedek_temsilci_telefon || ''}
                      onChange={(e) => setFormData({ ...formData, yedek_temsilci_telefon: e.target.value })}
                      placeholder="05XX XXX XX XX"
                    />
                  </div>
                  <div>
                    <Label htmlFor="yedek_temsilci_email">E-posta</Label>
                    <Input
                      id="yedek_temsilci_email"
                      type="email"
                      value={formData.yedek_temsilci_email || ''}
                      onChange={(e) => setFormData({ ...formData, yedek_temsilci_email: e.target.value })}
                      placeholder="yedek@email.com"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Yönetim Kurulu */}
          {currentStep === 5 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Yönetim Kurulu</h2>
                <Button type="button" variant="outline" size="sm" onClick={addYonetimKuruluMember}>
                  <Plus className="w-4 h-4 mr-1" />
                  Üye Ekle
                </Button>
              </div>
              
              {formData.yonetim_kurulu.length > 0 ? (
                <div className="space-y-4">
                  {formData.yonetim_kurulu.map((member, index) => (
                    <div key={`yk-form-${index}`} className="p-4 border rounded-lg space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Üye {index + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeYonetimKuruluMember(index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          placeholder="Ad *"
                          value={member.ad}
                          onChange={(e) => updateYonetimKuruluMember(index, 'ad', e.target.value)}
                        />
                        <Input
                          placeholder="Soyad *"
                          value={member.soyad}
                          onChange={(e) => updateYonetimKuruluMember(index, 'soyad', e.target.value)}
                        />
                        <Input
                          placeholder="Görev (Başkan, Üye, Sekreter...)"
                          value={member.gorev}
                          onChange={(e) => updateYonetimKuruluMember(index, 'gorev', e.target.value)}
                        />
                        <Input
                          placeholder="Telefon (05XX XXX XX XX)"
                          value={member.telefon || ''}
                          onChange={(e) => updateYonetimKuruluMember(index, 'telefon', e.target.value)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 space-y-2">
                  <p className="text-slate-500">Henüz yönetim kurulu üyesi eklenmedi</p>
                  <p className="text-xs text-slate-400">En az Başkan ve 2 üye eklemeniz önerilir</p>
                </div>
              )}
            </div>
          )}

          {/* Step 6: Faaliyet Bilgileri */}
          {currentStep === 6 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-900">Faaliyet Bilgileri</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="uye_sayisi">Üye Sayısı</Label>
                  <Input
                    id="uye_sayisi"
                    type="number"
                    value={formData.uye_sayisi}
                    onChange={(e) => setFormData({ ...formData, uye_sayisi: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="aktif_proje_sayisi">Aktif Proje Sayısı</Label>
                  <Input
                    id="aktif_proje_sayisi"
                    type="number"
                    value={formData.aktif_proje_sayisi}
                    onChange={(e) => setFormData({ ...formData, aktif_proje_sayisi: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="faaliyet_ozeti">Faaliyet Özeti</Label>
                <Textarea
                  id="faaliyet_ozeti"
                  value={formData.faaliyet_ozeti}
                  onChange={(e) => setFormData({ ...formData, faaliyet_ozeti: e.target.value })}
                  placeholder="Kurumunuzun faaliyetlerini özetleyin..."
                  rows={5}
                />
              </div>
            </div>
          )}

          {/* Step 7: Kent Konseyi Katılım */}
          {currentStep === 7 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-900">Kent Konseyi Katılım</h2>
              <div>
                <Label htmlFor="katilim_amaci">Katılım Amacı</Label>
                <Textarea
                  id="katilim_amaci"
                  value={formData.katilim_amaci}
                  onChange={(e) => setFormData({ ...formData, katilim_amaci: e.target.value })}
                  placeholder="Kent Konseyi'ne katılım amacınızı açıklayın..."
                  rows={4}
                />
              </div>
              <div>
                <Label>Katkı Alanları</Label>
                <p className="text-sm text-slate-500 mb-2">Katkıda bulunabileceğiniz alanları seçin</p>
                <div className="flex flex-wrap gap-2">
                  {katkiAlanlari.map((alan) => (
                    <button
                      key={alan}
                      type="button"
                      onClick={() => toggleKatkiAlani(alan)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        (formData.katki_alanlari || []).includes(alan)
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {alan}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 8: Belgeler */}
          {currentStep === 8 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-slate-900">Belgeler</h2>
              <p className="text-sm text-slate-500">
                Aşağıdaki belgeleri sırasıyla yükleyin. Zorunlu belgeler (*) ile işaretlenmiştir.
              </p>

              {/* Faaliyet Belgesi (DERBİS) */}
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-blue-600" />
                  <div>
                    <h3 className="font-medium text-slate-900">Faaliyet Belgesi (DERBİS) *</h3>
                    <p className="text-xs text-slate-500">Dernekler Bilgi Sistemi'nden alınan güncel faaliyet belgesi</p>
                  </div>
                </div>
                {formData.belgeler.find(b => b.belge_turu === 'derbis') ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-600" /><span className="text-sm text-emerald-700">{formData.belgeler.find(b => b.belge_turu === 'derbis').name}</span></div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({ ...formData, belgeler: formData.belgeler.filter(b => b.belge_turu !== 'derbis') })}><X className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ) : (
                  <div>
                    <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" id="derbis-upload"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        try {
                          const response = await filesAPI.upload(file);
                          setFormData(prev => ({ ...prev, belgeler: [...prev.belgeler, { id: response.data.file_id, name: file.name, belge_turu: 'derbis' }] }));
                          toast.success('Faaliyet belgesi yüklendi');
                        } catch { toast.error('Dosya yüklenemedi'); }
                        e.target.value = '';
                      }} />
                    <label htmlFor="derbis-upload">
                      <Button type="button" variant="outline" size="sm" asChild><span><Upload className="w-3 h-3 mr-1" /> Dosya Yükle</span></Button>
                    </label>
                  </div>
                )}
              </div>

              {/* Yönetim Kurulu Kararı */}
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-600" />
                  <div>
                    <h3 className="font-medium text-slate-900">Yönetim Kurulu Kararı *</h3>
                    <p className="text-xs text-slate-500">Kent Konseyi üyeliği için alınan yönetim kurulu karar belgesi</p>
                  </div>
                </div>
                {formData.belgeler.find(b => b.belge_turu === 'yonetim_kurulu_karari') ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-600" /><span className="text-sm text-emerald-700">{formData.belgeler.find(b => b.belge_turu === 'yonetim_kurulu_karari').name}</span></div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({ ...formData, belgeler: formData.belgeler.filter(b => b.belge_turu !== 'yonetim_kurulu_karari') })}><X className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ) : (
                  <div>
                    <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" id="yk-karar-upload"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        try {
                          const response = await filesAPI.upload(file);
                          setFormData(prev => ({ ...prev, belgeler: [...prev.belgeler, { id: response.data.file_id, name: file.name, belge_turu: 'yonetim_kurulu_karari' }] }));
                          toast.success('Yönetim kurulu kararı yüklendi');
                        } catch { toast.error('Dosya yüklenemedi'); }
                        e.target.value = '';
                      }} />
                    <label htmlFor="yk-karar-upload">
                      <Button type="button" variant="outline" size="sm" asChild><span><Upload className="w-3 h-3 mr-1" /> Dosya Yükle</span></Button>
                    </label>
                  </div>
                )}
              </div>

              {/* Yetkili Temsilci Belgesi (zorunlu) */}
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-rose-600" />
                  <div>
                    <h3 className="font-medium text-slate-900">Yetkili Temsilci Belgesi <span className="text-red-500">*</span></h3>
                    <p className="text-xs text-slate-500">Asıl temsilciyi yetkilendiren imzalı belge (kimlik fotokopisi veya yetki belgesi)</p>
                  </div>
                </div>
                {formData.belgeler.find(b => b.belge_turu === 'yetkili_temsilci') ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-600" /><span className="text-sm text-emerald-700">{formData.belgeler.find(b => b.belge_turu === 'yetkili_temsilci').name}</span></div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({ ...formData, belgeler: formData.belgeler.filter(b => b.belge_turu !== 'yetkili_temsilci') })}><X className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ) : (
                  <div>
                    <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" id="yetkili-temsilci-upload"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        try {
                          const response = await filesAPI.upload(file);
                          setFormData(prev => ({ ...prev, belgeler: [...prev.belgeler, { id: response.data.file_id, name: file.name, belge_turu: 'yetkili_temsilci' }] }));
                          toast.success('Yetkili temsilci belgesi yüklendi');
                        } catch { toast.error('Dosya yüklenemedi'); }
                        e.target.value = '';
                      }} />
                    <label htmlFor="yetkili-temsilci-upload">
                      <Button type="button" variant="outline" size="sm" asChild data-testid="yetkili-temsilci-upload-btn"><span><Upload className="w-3 h-3 mr-1" /> Dosya Yükle</span></Button>
                    </label>
                  </div>
                )}
              </div>

              {/* Tüzük / Ana Sözleşme */}
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  <div>
                    <h3 className="font-medium text-slate-900">Tüzük / Ana Sözleşme</h3>
                    <p className="text-xs text-slate-500">Kuruluş tüzüğü veya ana sözleşme belgesi</p>
                  </div>
                </div>
                {formData.belgeler.find(b => b.belge_turu === 'tuzuk') ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-600" /><span className="text-sm text-emerald-700">{formData.belgeler.find(b => b.belge_turu === 'tuzuk').name}</span></div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({ ...formData, belgeler: formData.belgeler.filter(b => b.belge_turu !== 'tuzuk') })}><X className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ) : (
                  <div>
                    <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" id="tuzuk-upload"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        try {
                          const response = await filesAPI.upload(file);
                          setFormData(prev => ({ ...prev, belgeler: [...prev.belgeler, { id: response.data.file_id, name: file.name, belge_turu: 'tuzuk' }] }));
                          toast.success('Tüzük belgesi yüklendi');
                        } catch { toast.error('Dosya yüklenemedi'); }
                        e.target.value = '';
                      }} />
                    <label htmlFor="tuzuk-upload">
                      <Button type="button" variant="outline" size="sm" asChild><span><Upload className="w-3 h-3 mr-1" /> Dosya Yükle</span></Button>
                    </label>
                  </div>
                )}
              </div>

              {/* Son Genel Kurul Tutanağı */}
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  <div>
                    <h3 className="font-medium text-slate-900">Son Genel Kurul Tutanağı</h3>
                    <p className="text-xs text-slate-500">En son yapılan genel kurul toplantı tutanağı</p>
                  </div>
                </div>
                {formData.belgeler.find(b => b.belge_turu === 'genel_kurul_tutanagi') ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-600" /><span className="text-sm text-emerald-700">{formData.belgeler.find(b => b.belge_turu === 'genel_kurul_tutanagi').name}</span></div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({ ...formData, belgeler: formData.belgeler.filter(b => b.belge_turu !== 'genel_kurul_tutanagi') })}><X className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ) : (
                  <div>
                    <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" id="gk-tutanak-upload"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        try {
                          const response = await filesAPI.upload(file);
                          setFormData(prev => ({ ...prev, belgeler: [...prev.belgeler, { id: response.data.file_id, name: file.name, belge_turu: 'genel_kurul_tutanagi' }] }));
                          toast.success('Genel kurul tutanağı yüklendi');
                        } catch { toast.error('Dosya yüklenemedi'); }
                        e.target.value = '';
                      }} />
                    <label htmlFor="gk-tutanak-upload">
                      <Button type="button" variant="outline" size="sm" asChild><span><Upload className="w-3 h-3 mr-1" /> Dosya Yükle</span></Button>
                    </label>
                  </div>
                )}
              </div>

              {/* Diğer Belgeler */}
              <div className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Paperclip className="w-5 h-5 text-slate-500" />
                  <div>
                    <h3 className="font-medium text-slate-900">Diğer Belgeler</h3>
                    <p className="text-xs text-slate-500">Ek belgeler (faaliyet raporları, referans yazıları vb.)</p>
                  </div>
                </div>
                {formData.belgeler.filter(b => b.belge_turu === 'diger').map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /><span className="text-sm">{file.name}</span></div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({ ...formData, belgeler: formData.belgeler.filter(b => b.id !== file.id) })}><X className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ))}
                <div>
                  <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" id="diger-upload"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      try {
                        const response = await filesAPI.upload(file);
                        setFormData(prev => ({ ...prev, belgeler: [...prev.belgeler, { id: response.data.file_id, name: file.name, belge_turu: 'diger' }] }));
                        toast.success('Belge yüklendi');
                      } catch { toast.error('Dosya yüklenemedi'); }
                      e.target.value = '';
                    }} />
                  <label htmlFor="diger-upload">
                    <Button type="button" variant="outline" size="sm" asChild><span><Upload className="w-3 h-3 mr-1" /> Dosya Ekle</span></Button>
                  </label>
                </div>
              </div>

              {/* Summary */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Yüklenen belge sayısı:</strong> {formData.belgeler.length} / 
                  {formData.belgeler.find(b => b.belge_turu === 'derbis') ? ' DERBİS ✓' : ' DERBİS ✗'} 
                  {formData.belgeler.find(b => b.belge_turu === 'yonetim_kurulu_karari') ? ' | YK Kararı ✓' : ' | YK Kararı ✗'}
                </p>
              </div>
            </div>
          )}

          {/* Step 9: Önizleme */}
          {currentStep === 9 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-slate-900">Başvuru Önizleme</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-medium text-slate-700 border-b pb-2">Temel Bilgiler</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Kurum Adı:</span>
                      <span className="font-medium">{formData.kurum_adi || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Tür:</span>
                      <span className="font-medium">{STK_KURUM_TURU_LABELS[formData.kurum_turu] || '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-medium text-slate-700 border-b pb-2">İletişim</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Telefon:</span>
                      <span className="font-medium">{formData.telefon || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">E-posta:</span>
                      <span className="font-medium">{formData.email || '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-medium text-slate-700 border-b pb-2">Temsilci</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Ad Soyad:</span>
                      <span className="font-medium">
                        {formData.temsilci_adi} {formData.temsilci_soyadi}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">E-posta:</span>
                      <span className="font-medium">{formData.temsilci_email || '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-medium text-slate-700 border-b pb-2">Belgeler</h3>
                  <div className="space-y-2 text-sm">
                    {formData.belgeler.length > 0 ? (
                      formData.belgeler.map((file) => (
                        <div key={file.id} className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600" />
                          <span>{file.name}</span>
                        </div>
                      ))
                    ) : (
                      <span className="text-slate-500">Belge yüklenmedi</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-800">Başvuruyu göndermeden önce</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Tüm bilgilerin doğruluğundan emin olun. Başvuru gönderildikten sonra değişiklik yapılamaz.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6 border-t mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Geri
            </Button>
            
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Kaydet
              </Button>
              
              {currentStep < 9 ? (
                <Button type="button" onClick={handleNext} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
                  Ileri
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="submit-stk-application-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Basvuruyu Gonder
                </Button>
              )}
            </div>
          </div>
          </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export { STKListPage, STKApplicationForm };
